import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import moment from 'moment';
import { Box } from '@material-ui/core';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { TableCell } from '@this/src/components/shared/ui/data_displays/table';
import type TicketingInstruction from '@this/src/domain/arrangement/ticketing_instruction';
import type { TicketingMethodKey } from '@this/src/domain/arrangement/ticketing_instruction';
import { TicketingMethod, TicketingMethodKeys } from '@this/src/domain/arrangement/ticketing_instruction';
import type Supplier from '@this/src/domain/supplier/supplier';
import type SuppliedItem from '@this/src/domain/supplied_item/supplied_item';
import type PaymentMethodList from '@this/src/domain/payment_method/payment_method_list';
import type IndividualRailwaySuppliedItem from '@this/domain/individual_railway_supplied_item/individual_railway_supplied_item';
import DatetimePicker from '@this/shared/datetime_picker/datetime_picker';
import { Fetcher } from '@this/src/util';
import SuppliedItemSelector from './supplied_item_selector';

// Helper function to check if object is array-like (supports both Array and MobX ObservableArray)
const isArrayLike = (obj: any): boolean => {
  return obj && typeof obj.map === 'function' && typeof obj.length === 'number';
};

interface Props {
  ticketingInstruction: TicketingInstruction;
  suppliers: Supplier[];
  suppliedItems: SuppliedItem[];
  paymentMethods: PaymentMethodList | null;
  individualRailwaySuppliedItems: IndividualRailwaySuppliedItem[];
  onChange: (ticketingInstruction: TicketingInstruction) => Promise<void>;
  isCompleted: boolean;
}

const getDefaultSnoozeTo = () => {
  const target = moment().add(1, 'day');
  return moment({
    years: target.get('year'),
    months: target.get('month'),
    days: target.get('date'),
    hours: 9,
    minutes: 0
  });
};

const TicketingInstructionActions: React.FC<Props> = ({
  ticketingInstruction: ti,
  suppliers,
  suppliedItems,
  paymentMethods,
  individualRailwaySuppliedItems,
  onChange,
  isCompleted
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingSnooze, setEditingSnooze] = useState<TicketingInstruction | null>(null);

  const handleClick = useCallback(async () => {
    setIsUpdating(true);
    try {
      await onChange(ti);
    } finally {
      setIsUpdating(false);
    }
  }, [ti, onChange]);

  const handleSaveSnooze = useCallback(
    async (instruction: TicketingInstruction) => {
      setIsUpdating(true);
      try {
        await Fetcher.post('/arrangement/order_item_snoozed_todos.json', {
          order_item_id: instruction.orderItemId,
          snooze_to: instruction.snoozeTo?.format('YYYY-MM-DD HH:mm:ss'),
          memo: instruction.snoozeMessage
        });
        ti.snoozeTo = instruction.snoozeTo;
        ti.snoozeMessage = instruction.snoozeMessage;
        setEditingSnooze(null);
      } finally {
        setIsUpdating(false);
      }
    },
    [ti]
  );

  const handleCancelSnooze = useCallback(async () => {
    setIsUpdating(true);
    try {
      await Fetcher.put('/arrangement/order_item_snoozed_todos/cancel.json', {
        order_item_id: ti.orderItemId
      });
      ti.snoozeTo = undefined;
      ti.snoozeMessage = undefined;
    } finally {
      setIsUpdating(false);
    }
  }, [ti]);

  return (
    <>
      <TableCell>
        <select
          onChange={e => {
            ti.ticketingMethod = e.target.value as TicketingMethodKey;
            const findIndividualRailwaySuppliedItems = individualRailwaySuppliedItems.find(
              item =>
                item.ticketingMethod === ti.ticketingMethod &&
                item.suppliedItemId != null &&
                item.suppliedItemId !== undefined
            );
            // 見つかった場合は、ticketingInstructionに設定
            if (findIndividualRailwaySuppliedItems) {
              ti.suppliedItemIds[0] = findIndividualRailwaySuppliedItems.suppliedItemId;
              const suppliedItem = suppliedItems.find(
                item => item.id === findIndividualRailwaySuppliedItems.suppliedItemId
              );
              if (suppliedItem) {
                ti.paymentMethodIds[0] = suppliedItem.paymentMethodId;
              }
            }
          }}
          disabled={isCompleted || !!ti.snoozeTo}
          value={ti.ticketingMethod || ''}
        >
          {TicketingMethodKeys.map(key => (
            <option key={key} value={key}>
              {TicketingMethod[key]}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell>
        {(() => {
          if (!ti.suppliedItemIds || !isArrayLike(ti.suppliedItemIds)) {
            return <div style={{ color: 'red' }}>Error: suppliedItemIds is not available</div>;
          }

          return ti.suppliedItemIds.map((id, index) => (
            <SuppliedItemSelector
              key={index}
              suppliers={suppliers}
              suppliedItems={suppliedItems}
              suppliedItemId={id}
              paymentMethods={paymentMethods}
              disabled={isCompleted || !!ti.snoozeTo}
              onChange={(newId: number | null) => {
                ti.suppliedItemIds[index] = newId;
                const suppliedItem = suppliedItems.find(item => item.id === newId);
                if (suppliedItem) {
                  ti.paymentMethodIds[index] = suppliedItem.paymentMethodId;
                }
              }}
              paymentMethodProps={{
                value: ti.paymentMethodIds?.[index],
                onChange: (newId: number | null) => {
                  if (ti.paymentMethodIds) {
                    ti.paymentMethodIds[index] = newId;
                  }
                }
              }}
            />
          ));
        })()}
      </TableCell>
      <TableCell>
        <Input
          value={ti.ticketingNote || ''}
          disabled={isCompleted || !!ti.snoozeTo}
          onChange={e => {
            ti.ticketingNote = e.target.value;
          }}
        />
      </TableCell>
      <TableCell>
        {isCompleted ? (
          <Button disabled>指示済み</Button>
        ) : (
          <Box>
            <Button loading={isUpdating} onClick={handleClick} disabled={!!ti.snoozeTo}>
              発券指示
            </Button>
            {ti.snoozeTo ? (
              <Box style={{ marginTop: '8px' }}>
                <Box>{ti.snoozeTo.format('YYYY-MM-DD HH:mm')}</Box>
                <Box>{ti.snoozeMessage}</Box>
                <Button size="small" loading={isUpdating} onClick={handleCancelSnooze}>
                  TODOに戻す
                </Button>
              </Box>
            ) : editingSnooze && editingSnooze.orderItemId === ti.orderItemId ? (
              <Box style={{ marginTop: '8px' }}>
                <DatetimePicker
                  value={editingSnooze.snoozeTo}
                  onChange={d => {
                    editingSnooze.snoozeTo = d;
                  }}
                  showTime
                  showPast={false}
                  disabledDays={0}
                />
                <input
                  type="text"
                  placeholder="メモ"
                  value={editingSnooze.snoozeMessage}
                  onChange={e => {
                    editingSnooze.snoozeMessage = e.target.value;
                  }}
                  style={{ marginTop: '4px', width: '100%' }}
                />
                <Box display="flex" style={{ marginTop: '4px' }}>
                  <Button size="small" loading={isUpdating} onClick={() => setEditingSnooze(null)}>
                    キャンセル
                  </Button>
                  <Button
                    size="small"
                    style={{ marginLeft: '10px' }}
                    loading={isUpdating}
                    onClick={() => handleSaveSnooze(editingSnooze)}
                  >
                    保存
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box style={{ marginTop: '8px' }}>
                <Button
                  size="small"
                  loading={isUpdating}
                  onClick={() => {
                    const et = _.clone(ti);
                    et.snoozeTo = getDefaultSnoozeTo();
                    setEditingSnooze(et);
                  }}
                >
                  スヌーズ
                </Button>
              </Box>
            )}
          </Box>
        )}
      </TableCell>
    </>
  );
};

export default observer(TicketingInstructionActions);
